export type ColorSelectorOption = {
    value: string;
    name: string;
    imageUrl: string;
};

export type ColorSelectorOptions = {
    title?: string;
    options: ColorSelectorOption[];
    preselected?: string[];
};

function isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function parseColorSelectorOption(item: unknown): ColorSelectorOption | null {
    if (!isRecord(item)) {
        return null;
    }

    const { value, name, imageUrl } = item;

    if (typeof value !== 'string' || value === '') {
        return null;
    }
    if (typeof name !== 'string' || name === '') {
        return null;
    }
    if (typeof imageUrl !== 'string' || imageUrl === '') {
        return null;
    }

    return { value, name, imageUrl };
}

/**
 * Parses the assistant-side color-selector question payload that drives the composer selector.
 *
 * Accepts `unknown` input and returns a validated `ColorSelectorOptions` or `null` on any
 * validation failure. `title` and `preselected` are optional and may be entirely absent — the
 * live payload carries neither. Lenient on unknown fields; only known fields are extracted.
 *
 * @param data - The input to parse (expected to be the assistant's `component.data`)
 * @returns A validated `ColorSelectorOptions` object, or `null` if validation fails
 */
export function parseColorSelectorOptions(data: unknown): ColorSelectorOptions | null {
    if (!isRecord(data)) {
        return null;
    }

    const { title, options, preselected } = data;

    if (!Array.isArray(options) || options.length === 0) {
        return null;
    }

    const parsedOptions: ColorSelectorOption[] = [];
    for (const item of options) {
        const parsed = parseColorSelectorOption(item);
        if (!parsed) {
            return null;
        }
        parsedOptions.push(parsed);
    }

    if (title !== undefined && typeof title !== 'string') {
        return null;
    }

    if (preselected !== undefined) {
        if (!Array.isArray(preselected) || preselected.some((value) => typeof value !== 'string')) {
            return null;
        }
    }

    return {
        ...(title !== undefined ? { title } : {}),
        options: parsedOptions,
        ...(preselected !== undefined ? { preselected: preselected as string[] } : {}),
    };
}
